import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { Config } from '@/config';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  modifyModalRecoil,
  modelModifyModalRecoil,
} from '@/atom/ModalAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import ModalType1 from '@/modal/ModalType1';
import { ModelApi } from '@/sdk/ModelApi';
import { DistributorApi } from '@/sdk/DistributorApi';
import {
  UpdateModelDtoTx,
  ModelControllerGetModelDuplicateParams,
  DistributorControllerGetDistributorsParams,
} from '@/sdk/data-contracts';
import { JsonEditor } from 'json-edit-react';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/worker-json';
import { distributorListRecoil } from '@/atom/DistributorAtom';

interface ModelModifyModalProps {
  id: number;
}

const ModelModifyModal = ({ id }: ModelModifyModalProps) => {
  const modelApi = new ModelApi({ baseURL: Config.API_URL });
  const distributorApi = new DistributorApi({ baseURL: Config.API_URL });
  const navigate = useNavigate();

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const setModelModifyModalState = useSetRecoilState(modelModifyModalRecoil);
  const [distributorListState, setDistributorListState] = useRecoilState(
    distributorListRecoil,
  );

  const [name, setName] = useState<string>('');
  const [usuallyName, setUsuallyName] = useState<string>('');
  const [nameDuplication, setNameDuplication] = useState<boolean>(true);
  const [manufacturer, setManufacturer] = useState<string>('');
  const [transmissionCycle, setTransmissionCycle] = useState<number | null>(
    null,
  );
  const [distributorId, setDistributorId] = useState<number | null>(null);
  const [appEUI, setAppEUI] = useState<string>('');
  const [uKey, setUKey] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [parserType, setParserType] = useState<string>('ASCII');
  const [separator, setSeparator] = useState<string>('');
  const [collectionCycle, setCollectionCycle] = useState<number | null>(null);
  const [protocol, setProtocol] = useState<string>('');
  const [isDid, setIsDid] = useState<boolean>(false);
  const [jsonType, setJsonType] = useState<boolean>(true);
  const [jsonData, setJsonData] = useState<any>({
    title: '',
    description: '',
    encrypted: false,
    properties: {
      data: {
        type: 'object',
        sort: 1,
        loop: true,
        $count: 'count',
        properties: {
          key: 'value',
        },
      },
    },
  });

  const fetchDistributorList = async () => {
    const dto: DistributorControllerGetDistributorsParams = {
      sortBy: 'createdAt',
      order: 'desc',
    };

    try {
      const { data } =
        await distributorApi.distributorControllerGetDistributors(dto, {
          headers: {
            Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
            'Content-Type': 'application/json',
          },
        });
      if (data.results) {
        setDistributorListState(data.results);
        return data.results;
      }
    } catch (err: any) {}
  };

  const fetchModelModify = async () => {
    try {
      const distributorList = await fetchDistributorList();

      const { data } = await modelApi.modelControllerGetModelData(Number(id), {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });

      setName(data.name);
      setUsuallyName(data.name);
      setManufacturer(data.manufacturer);
      setTransmissionCycle(data.transmissionCycle);
      setDistributorId(
        Number(
          distributorList?.find((list) => list.name === data.distributorName)
            ?.id,
        ),
      );
      setAppEUI(data.appEUI);
      setUKey(data.uKey);
      setDescription(data.description);
      setParserType(data.parserType);
      setSeparator(data.separator);
      setCollectionCycle(data.collectionCycle);
      setProtocol(data.protocol);
      setIsDid(data.isDid);
      setJsonData(data.model);
    } catch (err: any) {}
  };

  const handleEditorChange = (newValue: string) => {
    try {
      const parsedData = JSON.parse(newValue);
      setJsonData(parsedData);
    } catch (e) {}
  };

  const handleNameDuplication = async () => {
    if (name.trim() === '') {
      setErrorModalText('모델명을 입력해 주세요.');
      return;
    }

    const dto: ModelControllerGetModelDuplicateParams = {
      name,
    };

    try {
      const data = await modelApi.modelControllerGetModelDuplicate(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      if (data.data.message === '입력하신 모델명의 모델이 이미 존재합니다.') {
        setErrorModalText('해당 모델명은 존재합니다.');
        return;
      }
      setNameDuplication(true);
    } catch (err: any) {}
  };

  const handleSubmit = async () => {
    if (
      manufacturer.trim() === '' ||
      transmissionCycle === null ||
      appEUI.trim() === '' ||
      uKey.trim() === '' ||
      separator.trim() === ''
    ) {
      setErrorModalText('빈 칸을 확인해 주세요.');
      return;
    }

    if (!nameDuplication) {
      setErrorModalText('모델명 중복 확인을 해주세요.');
      return;
    }

    const dto: UpdateModelDtoTx = {
      name,
      manufacturer,
      transmissionCycle: Number(transmissionCycle),
      distributorId: Number(distributorId),
      appEUI,
      uKey,
      description,
      parserType,
      separator,
      collectionCycle: Number(collectionCycle),
      protocol,
      isDid,
      model: jsonData,
    };

    try {
      await modelApi.modelControllerUpdateModel(id, dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      setCompleteModalState(true);
    } catch (err: any) {}
  };

  useEffect(() => {
    fetchModelModify();
  }, []);

  return (
    <>
      <ModalType1 width={1000}>
        <div className="title-box flex aic jcsb">
          <h1>모델 수정</h1>
          <button
            className="x-btn img-box"
            onClick={() => setModelModifyModalState(false)}
          >
            <img src="/img/ico_x_444.svg" alt="" />
          </button>
        </div>
        <div className="cnt-area flex fdc">
          <div className="cnt-wrap flex fdc">
            <div className="cnt-box flex fdc">
              <div className="cnt-title-box flex jcsb aie">
                <div className="left-box">
                  <h2 className="tm">기본 정보</h2>
                </div>
                <div className="right-box flex">
                  <h3>
                    <span className="inblock">*</span>필수 항목
                  </h3>
                </div>
              </div>
              <CS.TableType2>
                <table>
                  <tbody>
                    <tr>
                      <th>
                        모델명<span className="star-point">*</span>
                      </th>
                      <td>
                        <div className="td-box">
                          <IS.TextInputType1
                            style={{ width: 190 }}
                            type="text"
                            value={name}
                            onChange={(e) => {
                              setName(e.target.value);
                              if (e.target.value === usuallyName) {
                                setNameDuplication(true);
                              } else {
                                setNameDuplication(false);
                              }
                            }}
                          />
                          <BS.Btn3Type3
                            type="button"
                            onClick={handleNameDuplication}
                            disabled={nameDuplication}
                          >
                            {nameDuplication ? '완료' : '중복 조회'}
                          </BS.Btn3Type3>
                        </div>
                      </td>
                      <th>
                        제조사<span className="star-point">*</span>
                      </th>
                      <td>
                        <IS.TextInputType1
                          style={{ width: 190 }}
                          type="text"
                          value={manufacturer}
                          onChange={(e) => setManufacturer(e.target.value)}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>
                        전송주기<span className="star-point">*</span>
                      </th>
                      <td>
                        <div className="td-box">
                          <IS.TextInputType1
                            style={{ width: 190 }}
                            type="text"
                            value={
                              transmissionCycle === null
                                ? ''
                                : String(transmissionCycle)
                            }
                            onChange={(e) => {
                              const value = e.target.value;
                              if (!isNaN(Number(value)) || value === '') {
                                setTransmissionCycle(
                                  value === '' ? null : Number(value),
                                );
                              }
                            }}
                          />
                          분
                        </div>
                      </td>
                      <th>총판이름</th>
                      <td>
                        <IS.SelectType1 style={{ width: 190 }}>
                          <select
                            value={String(distributorId)}
                            onChange={(e) =>
                              setDistributorId(Number(e.target.value))
                            }
                            className="selected"
                          >
                            <option value="">선택</option>
                            {distributorListState.map((list) => (
                              <option key={list.id} value={list.id}>
                                {list.name}
                              </option>
                            ))}
                          </select>
                          <span className="img-box">
                            <img src="/img/arrow_bottom_555.svg" alt="" />
                          </span>
                        </IS.SelectType1>
                      </td>
                    </tr>
                    <tr>
                      <th>
                        AppEUI<span className="star-point">*</span>
                      </th>
                      <td colSpan={3}>
                        <IS.TextInputType1
                          style={{ width: 820 }}
                          type="text"
                          value={appEUI}
                          onChange={(e) => setAppEUI(e.target.value)}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>
                        uKey<span className="star-point">*</span>
                      </th>
                      <td colSpan={3}>
                        <IS.TextInputType1
                          style={{ width: 820 }}
                          type="text"
                          value={uKey}
                          onChange={(e) => setUKey(e.target.value)}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>모델설명</th>
                      <td colSpan={3}>
                        <IS.TextInputType1
                          style={{ width: 820 }}
                          type="text"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </CS.TableType2>
            </div>
            <div className="cnt-box flex fdc">
              <div className="cnt-title-box flex jcsb aie">
                <div className="left-box">
                  <h2 className="tm">데이터 정보</h2>
                </div>
              </div>
              <CS.TableType2>
                <table>
                  <tbody>
                    <tr>
                      <th>파서 타입</th>
                      <td>
                        <IS.SelectType1 style={{ width: 190 }}>
                          <select
                            value={parserType}
                            onChange={(e) => setParserType(e.target.value)}
                            className="selected"
                          >
                            <option value="ASCII">ASCII</option>
                            <option value="BINARY ">BINARY </option>
                          </select>
                          <span className="img-box">
                            <img src="/img/arrow_bottom_555.svg" alt="" />
                          </span>
                        </IS.SelectType1>
                      </td>
                      <th>
                        데이터간 구분자<span className="star-point">*</span>
                      </th>
                      <td>
                        <IS.SelectType1 style={{ width: 190 }}>
                          <select
                            value={separator}
                            onChange={(e) => setSeparator(e.target.value)}
                            className="selected"
                          >
                            <option value="">선택</option>
                            <option value="|(파이프)">|(파이프)</option>
                            <option value=",(Comma)">,(Comma)</option>
                            <option value="CRLF(캐리지리턴)">
                              CRLF(캐리지리턴)
                            </option>
                            <option value="$">$</option>
                            <option value="#">#</option>
                            <option value="%">%</option>
                            <option value="^">^</option>
                            <option value="&">&</option>
                            <option value="(공백-blank)">(공백-blank)</option>
                          </select>
                          <span className="img-box">
                            <img src="/img/arrow_bottom_555.svg" alt="" />
                          </span>
                        </IS.SelectType1>
                      </td>
                    </tr>
                    <tr>
                      <th>프로토콜</th>
                      <td>
                        <IS.SelectType1 style={{ width: 190 }}>
                          <select
                            value={protocol}
                            onChange={(e) => setProtocol(e.target.value)}
                            className="selected"
                          >
                            <option value="">선택</option>
                            <option value="LTE">LTE</option>
                            <option value="LoRa">LoRa</option>
                          </select>
                          <span className="img-box">
                            <img src="/img/arrow_bottom_555.svg" alt="" />
                          </span>
                        </IS.SelectType1>
                      </td>
                      <th>수집 주기</th>
                      <td>
                        <div className="td-box">
                          <IS.TextInputType1
                            style={{ width: 190 }}
                            type="text"
                            value={
                              collectionCycle === null
                                ? ''
                                : String(collectionCycle)
                            }
                            onChange={(e) => {
                              const value = e.target.value;
                              if (!isNaN(Number(value)) || value === '') {
                                setCollectionCycle(
                                  value === '' ? null : Number(value),
                                );
                              }
                            }}
                          />
                          분
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>JSON</th>
                      <td>
                        <CS.Gap24Box>
                          <IS.CheckTextBoxType2>
                            <input
                              type="radio"
                              id="jsonTypeTree"
                              name="jsonType"
                              checked={jsonType}
                              onChange={() => setJsonType(true)}
                            />
                            <label htmlFor="jsonTypeTree">
                              <span className="img-box">
                                <img src="/img/ico_check_3.svg" alt="" />
                              </span>
                              <span className="text">tree</span>
                            </label>
                          </IS.CheckTextBoxType2>
                          <IS.CheckTextBoxType2>
                            <input
                              type="radio"
                              id="jsonTypeEditor"
                              name="isJsonType"
                              checked={!jsonType}
                              onChange={() => setJsonType(false)}
                            />
                            <label htmlFor="jsonTypeEditor">
                              <span className="img-box">
                                <img src="/img/ico_check_3.svg" alt="" />
                              </span>
                              <span className="text">editor</span>
                            </label>
                          </IS.CheckTextBoxType2>
                        </CS.Gap24Box>
                      </td>
                      <th>DID 검증</th>
                      <td>
                        <CS.Gap24Box>
                          <IS.CheckTextBoxType2>
                            <input
                              type="radio"
                              id="isDidOff"
                              name="isDid"
                              checked={!isDid}
                              onChange={() => setIsDid(false)}
                            />
                            <label htmlFor="isDidOff">
                              <span className="img-box">
                                <img src="/img/ico_check_3.svg" alt="" />
                              </span>
                              <span className="text">OFF</span>
                            </label>
                          </IS.CheckTextBoxType2>
                          <IS.CheckTextBoxType2>
                            <input
                              type="radio"
                              id="isDidOn"
                              name="isDid"
                              checked={isDid}
                              onChange={() => setIsDid(true)}
                            />
                            <label htmlFor="isDidOn">
                              <span className="img-box">
                                <img src="/img/ico_check_3.svg" alt="" />
                              </span>
                              <span className="text">ON</span>
                            </label>
                          </IS.CheckTextBoxType2>
                        </CS.Gap24Box>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </CS.TableType2>
            </div>
            <div className="cnt-box flex fdc">
              <div className="cnt-title-box flex jcsb aie">
                <div className="left-box">
                  <h2 className="tm">데이터 정보</h2>
                </div>
              </div>
              {jsonType && (
                <JsonEditorBox>
                  <JsonEditor
                    data={jsonData}
                    setData={setJsonData}
                    rootName=""
                    theme="githubLight"
                    // onAdd={({ name }) => {
                    //   if (name === 'city') setUpdateName('city');
                    //   if (name === '건물') setUpdateName('건물');
                    // }}
                  />
                </JsonEditorBox>
              )}
              {!jsonType && (
                <AceEditorBox>
                  <AceEditor
                    mode="json"
                    theme="github"
                    value={JSON.stringify(jsonData, null, 2)}
                    onChange={handleEditorChange}
                    name="json-editor"
                    fontSize={16}
                    lineHeight={24}
                    editorProps={{ $blockScrolling: true }}
                    width="100%"
                    height="500px"
                    setOptions={{
                      useWorker: false,
                      showPrintMargin: false,
                    }}
                  />
                </AceEditorBox>
              )}
            </div>
          </div>
          <div className="btn-wrap">
            <BS.Btn2Type3
              type="button"
              onClick={() => setModelModifyModalState(false)}
            >
              취소
            </BS.Btn2Type3>
            <BS.Btn2Type4 type="button" onClick={handleSubmit}>
              수정
            </BS.Btn2Type4>
          </div>
        </div>
      </ModalType1>
    </>
  );
};

export default ModelModifyModal;

export const JsonEditorBox = styled.div`
  width: 100%;
  .jer-collection-name {
    display: flex;
    gap: 4px;
  }
  * {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Droid Sans Mono', 'Consolas',
      monospace !important;
    font-weight: 400 !important;
    letter-spacing: 0 !important;
    -webkit-appearance: revert;
    appearance: revert;
  }
`;

export const AceEditorBox = styled.div`
  * {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Droid Sans Mono', 'Consolas',
      monospace !important;
    font-weight: 400 !important;
    letter-spacing: 0 !important;
  }
`;
